import { Box, Chip, IconButton, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useEffect, useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { SalesOrderLinePriceOverrideDialog } from "./SalesOrderLinePriceOverrideDialog"
import { SalesOrderLineQuantityUomSelect } from "./SalesOrderLineQuantityUomSelect"

import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { UOM_RENDER_PRECISION, UOM_ROUNDING_PRECISION } from "@/constants/formats"
import { ComputedProductStatus, type Product } from "@/graphql/codegen/graphql"
import { ComputedProductStatusMap } from "@/screens/Companies/products/utils/productStatusMap"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type { SalesOrderLogisticsFormValues } from "@/screens/CustomerOrders/schemas"
import { PRODUCT_STOCK_TYPE_CHIP_COLOR, PRODUCT_STOCK_TYPE_LABEL_MAP } from "@/screens/Products/constants"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import type { Option } from "@/types/form-fields"
import { formatNumber } from "@/utils/number"

interface SalesOrderLineProps {
  index: number
  cpoNumber: string
  product: Product
  readOnly?: boolean
}

export const SalesOrderLine: FC<SalesOrderLineProps> = ({ index, cpoNumber, product, readOnly = false }) => {
  const [isPriceOverrideOpen, setIsPriceOverrideOpen] = useState(false)

  const { isReadonly: isPermissionReadonly } = useSalesOrderPermissions()
  const isReadonly = isPermissionReadonly || readOnly
  const { control, setValue } = useFormContext<SalesOrderLogisticsFormValues>()

  const basePath = `lines.${index}` as const
  const line = useWatch({ control, name: basePath })

  const total = (line.pricePerUom ?? 0) * (line.quantityInUom ?? 0)

  const { units, getUnits } = useUnitOfMeasurementsStore(
    useShallow((state) => ({ units: state.units, getUnits: state.getUnits }))
  )
  useEffect(() => {
    if (!units.length) getUnits()
  }, [units.length, getUnits])

  // Not product-scoped like createUomsList (PO/CPO) — a Sales Order line has no product to scope
  // by yet, so this lists every company UoM until that data lands on the line.
  const uomOptions: Option<string>[] = useMemo(
    () => units.map((uom) => ({ value: uom.id, label: uom.name ?? uom.id })),
    [units]
  )

  const priceUomId = line.priceUomId ?? line.uomId
  const priceUomLabel = units.find((uom) => uom.id === priceUomId)?.name ?? priceUomId

  const handlePriceOverrideSubmit = ({ pricePerUom }: { pricePerUom: number | null }) => {
    setValue(`${basePath}.pricePerUom`, pricePerUom, { shouldDirty: true })
    setValue(`${basePath}.priceUomId`, line.uomId, { shouldDirty: true })
    setValue(`${basePath}.isManualPriceSet`, true, { shouldDirty: true })
    setIsPriceOverrideOpen(false)
  }

  return (
    <Box className='flex items-start gap-6 px-4 py-6'>
      <FormDatePicker
        control={control}
        name={`${basePath}.deliverDate`}
        label='Deliver By Date'
        disabled={isReadonly}
        className='w-40'
        data-testid={`customer-orders.so-line-items.deliver-by-date-field-${index}`}
      />

      <Box className='flex w-28 flex-col'>
        <Typography variant='caption' className='mb-2 text-xs leading-[0.875rem] text-gray-700'>
          Customer PO
        </Typography>
        <Typography
          variant='body2'
          className='text-primary'
          data-testid={`customer-orders.so-line-items.customer-po-${index}`}
        >
          {cpoNumber}
        </Typography>
      </Box>

      <Box
        className='flex w-32 flex-col items-start gap-1'
        data-testid={`customer-orders.so-line-items.product-id-${index}`}
      >
        <Box className='flex items-center gap-2'>
          <StatusIndicator
            status={product.computedProductStatus ?? ComputedProductStatus.Inactive}
            map={ComputedProductStatusMap}
          />
          <Typography variant='body2' className='font-bold text-gray-700'>
            {product.publicId}
          </Typography>
        </Box>
        <DisplayAS400Id
          id={product.externalSystemId}
          data-testid={`customer-orders.so-line-items.as400-chip-${index}`}
        />
        {product.stockType && (
          <Chip
            label={PRODUCT_STOCK_TYPE_LABEL_MAP[product.stockType]}
            color={PRODUCT_STOCK_TYPE_CHIP_COLOR[product.stockType]}
            size='small'
            data-testid={`customer-orders.so-line-items.stock-chip-${index}`}
          />
        )}
      </Box>

      <Box className='flex w-48 flex-col gap-1' data-testid={`customer-orders.so-line-items.product-${index}`}>
        <Typography variant='body2' className='font-bold text-gray-800'>
          {product.companyProductDescription}
        </Typography>
        {product.vendorDescription && (
          <Typography variant='body2' className='text-gray-700'>
            {product.vendorDescription}
          </Typography>
        )}
      </Box>

      <FormNumericField
        control={control}
        name={`${basePath}.pallets`}
        label='Pallets'
        required
        hideErrorMessage
        disabled={isReadonly}
        className='w-28'
        data-testid={`customer-orders.so-line-items.pallets-field-${index}`}
      />

      <FormNumericField
        control={control}
        name={`${basePath}.units`}
        label='Units'
        required
        hideErrorMessage
        disabled={isReadonly}
        className='w-28'
        data-testid={`customer-orders.so-line-items.units-field-${index}`}
      />

      <FormNumericField
        control={control}
        name={`${basePath}.quantityInUom`}
        label='Quantity'
        required
        hideErrorMessage
        disabled={isReadonly}
        className='w-40'
        slotProps={{
          input: {
            endAdornment: <SalesOrderLineQuantityUomSelect index={index} options={uomOptions} disabled={isReadonly} />,
          },
        }}
        data-testid={`customer-orders.so-line-items.quantity-field-${index}`}
      />

      <Box className='flex flex-1 flex-col items-end gap-1'>
        <Box className='flex items-center gap-1'>
          <Typography
            variant='h6'
            className='font-bold text-gray-700'
            data-testid={`customer-orders.so-line-items.price-${index}`}
          >
            ${formatNumber(line.pricePerUom ?? 0, UOM_ROUNDING_PRECISION)} /{priceUomLabel}
          </Typography>
          <IconButton
            color='primary'
            size='small'
            disabled={isReadonly}
            onClick={() => setIsPriceOverrideOpen(true)}
            data-testid={`customer-orders.so-line-items.edit-price-button-${index}`}
          >
            <EditOutlined />
          </IconButton>
        </Box>
        <Typography variant='caption' className='text-gray-700'>
          Total: ${formatNumber(total, UOM_RENDER_PRECISION)}
        </Typography>
      </Box>

      {isPriceOverrideOpen && (
        <SalesOrderLinePriceOverrideDialog
          pricePerUom={line.pricePerUom}
          onClose={() => setIsPriceOverrideOpen(false)}
          onSubmit={handlePriceOverrideSubmit}
        />
      )}
    </Box>
  )
}
